import React, { ReactElement, useMemo } from 'react';

import { MemberLeaderboard as MemberLeaderboardType, MemberScore } from '../../../../../types/entity.types';
import { MemberLeaderboardTableData } from '../../../../../types/common.types';

import { formatUsername, isEmpty } from '../../../../../utils/util';

import Button from '../../../../common/button/Button';
import Table from '../../../../common/table/Table';

import './leaderboard.scss';

const DATA_TEMPLATE: MemberLeaderboardTableData = {
  '#': '',
  username: '',
  'weight(%)': '',
  "cm's": '',
  steps: '',
  objectives: '',
  points: ''
};

type Props = {
  leaderboard?: MemberLeaderboardType;
  downloadExcel: () => void;
};

const MemberLeaderboard = ({ leaderboard, downloadExcel }: Props): ReactElement => {
  const scores: MemberLeaderboardTableData[] = useMemo((): MemberLeaderboardTableData[] => {
    if (!leaderboard?.userScores) return [];

    return leaderboard.userScores.map(
      (score: MemberScore, index: number): MemberLeaderboardTableData => ({
        '#': index + 1,
        username: formatUsername(score.username),
        'weight(%)': score.weight.toString(),
        "cm's": score.cm.toString(),
        steps: score.steps.toString(),
        objectives: score.objectives.toString(),
        points: score.points.toString()
      })
    );
  }, [leaderboard]);

  const renderTableBottomActions = (): ReactElement => (
    <div className="leaderboard__table-bottom-actions">
      <Button text="Download .xlsx" onClick={downloadExcel} disabled={isEmpty(scores)} />
    </div>
  );

  return (
    <>
      <section className="leaderboard">
        <section className="leaderboard__overview">
          <section className="leaderboard__overview__winner">
            <p>Overall Winner:</p>
            <div>
              <p>{formatUsername(leaderboard?.overallWinnerUsername) ?? 'N/A'}</p>
              <p>{leaderboard?.overallWinnerAmount ?? '0'} pts</p>
            </div>
          </section>
          <section className="leaderboard__overview__stats">
            <div className="leaderboard__overview__stat">
              <p>Most weight lost (%):</p>
              <p>{formatUsername(leaderboard?.mostWeightUsername) ?? 'N/A'}</p>
              <p>{leaderboard?.mostWeightPercentage ?? '0'}%</p>
            </div>
            <div className="leaderboard__overview__stat">
              <p>Most cm's lost:</p>
              <p>{formatUsername(leaderboard?.mostCMsUsername) ?? 'N/A'}</p>
              <p>{leaderboard?.mostCMsAmount ?? '0'} cm</p>
            </div>
            <div className="leaderboard__overview__stat">
              <p>Most Steps:</p>
              <p>{formatUsername(leaderboard?.mostStepsUsername) ?? 'N/A'}</p>
              <p>{leaderboard?.mostStepsAmount ?? '0'} steps</p>
            </div>
            <div className="leaderboard__overview__stat">
              <p>Most Objectives Met:</p>
              <p>{formatUsername(leaderboard?.mostObjectivesUsername) ?? 'N/A'}</p>
              <p>{leaderboard?.mostObjectivesAmount ?? '0'} objectives</p>
            </div>
          </section>
        </section>
        <Table
          data={scores}
          dataTemplate={DATA_TEMPLATE}
          rowsPerPage={10}
          bottomActions={renderTableBottomActions()}
          filter
        />
      </section>
    </>
  );
};

export default MemberLeaderboard;
